/**
 * Quiz typed fetchers — CRUD + host-role management (P1).
 * Backend: apps/api quiz module. Contracts: @system/shared quiz.contract.ts.
 */
import type {
  ApiResponse, QuizAccess, QuizGameDetail, QuizGameListEntry, QuizHostEntry, QuizListEntry, QuizRecord, QuizSaveInput, QuizStats,
  QuizTeamBuildingOption,
} from '@system/shared'
import { apiFetch } from './api-client-core'

export async function getQuizMyAccess(): Promise<ApiResponse<QuizAccess>> {
  return apiFetch<QuizAccess>('/api/quiz/my-access')
}

export async function getQuizzes(): Promise<ApiResponse<QuizListEntry[]>> {
  return apiFetch<QuizListEntry[]>('/api/quiz/quizzes')
}

/** The Team Building events a game can be played in the teams of (host only). */
export async function getQuizTeamBuildingOptions(): Promise<ApiResponse<QuizTeamBuildingOption[]>> {
  return apiFetch<QuizTeamBuildingOption[]>('/api/quiz/team-building-events')
}

export async function getQuiz(id: string): Promise<ApiResponse<QuizRecord>> {
  return apiFetch<QuizRecord>(`/api/quiz/quizzes/${encodeURIComponent(id)}`)
}

export async function createQuiz(input: QuizSaveInput): Promise<ApiResponse<QuizRecord>> {
  return apiFetch<QuizRecord>('/api/quiz/quizzes', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  })
}

export async function updateQuiz(id: string, input: QuizSaveInput): Promise<ApiResponse<QuizRecord>> {
  return apiFetch<QuizRecord>(`/api/quiz/quizzes/${encodeURIComponent(id)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  })
}

export async function deleteQuiz(id: string): Promise<ApiResponse<{ ok: boolean }>> {
  return apiFetch<{ ok: boolean }>(`/api/quiz/quizzes/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function getQuizHosts(): Promise<ApiResponse<QuizHostEntry[]>> {
  return apiFetch<QuizHostEntry[]>('/api/quiz/hosts')
}

export async function addQuizHost(email: string): Promise<ApiResponse<{ ok: boolean }>> {
  return apiFetch<{ ok: boolean }>('/api/quiz/hosts', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
  })
}

export async function removeQuizHost(email: string): Promise<ApiResponse<{ ok: boolean }>> {
  return apiFetch<{ ok: boolean }>(`/api/quiz/hosts/${encodeURIComponent(email)}`, { method: 'DELETE' })
}

export async function getQuizGames(): Promise<ApiResponse<QuizGameListEntry[]>> {
  return apiFetch<QuizGameListEntry[]>('/api/quiz/games')
}

export async function getQuizGameDetail(code: string): Promise<ApiResponse<QuizGameDetail>> {
  return apiFetch<QuizGameDetail>(`/api/quiz/games/${encodeURIComponent(code)}`)
}

export async function deleteQuizGame(code: string): Promise<ApiResponse<{ ok: boolean }>> {
  return apiFetch<{ ok: boolean }>(`/api/quiz/games/${encodeURIComponent(code)}`, { method: 'DELETE' })
}

export async function getQuizStats(): Promise<ApiResponse<QuizStats>> {
  return apiFetch<QuizStats>('/api/quiz/stats')
}
